/**
 * SketchObjectGizmoBinding — TransformGizmo binding for SKETCH_OBJECT
 * annotations (furniture/fixture/structure symbols in the layout).
 *
 * Sketch objects are PROPERTY-driven: their transform lives in reactive
 * properties (anchor / rotation / width / depth / wall placement) whose
 * writes auto-rebuild the glyph and auto-persist. The binding maps the
 * gizmo's cumulative deltas onto those properties:
 *  - translation → anchor shift (FREE) or wall offset/inset (WALL),
 *  - rotation → anchor rotated about the center + the rotation property
 *    (FREE only — wall objects take their orientation from the wall),
 *  - scale X → the WIDTH size parameter (LINE-anchored runs get their two
 *    anchor endpoints rebuilt symmetrically — width is derived from the
 *    segment there), scale Y → the DEPTH size parameter.
 *
 * FREE transforms run through the same COLLISION/assist resolution the
 * placement flows use ({@link resolveFreePlacementAt}): near a wall the
 * object aligns flush at the nearest quarter-turn and clamps inside the
 * wall span (it can't be pushed through walls or corners), translations
 * additionally get the peer smart-guide snap. Holding SHIFT overrides —
 * the raw transform applies. Wall-attached objects clamp their offset to
 * the edge span and keep inset ≥ 0 (structural, not overridable).
 *
 * The gizmo frame is the object's own frame (`resolveFrame`): local X =
 * width axis, local Y = depth axis for every anchor kind — with space
 * 'object' the scale handles stay object-aligned even in the 2D views.
 *
 * One undo snapshot per drag sweep restores placement AND width/depth.
 */

import * as THREE from 'three';
import { getSketchObjectWallOffsetProperty, getSketchObjectWidthProperty, getSketchObjectDepthProperty } from '@pointorama/potree-shared';
import { Snapshot } from '../../ToolUndoRedoManager';
import { Viewer } from '../../../../viewer/viewer';
import { PointoramaSketchObject } from '../../../../utils/PointoramaSketchObject';
import { PointoramaWallSketch } from '../../../../utils/PointoramaWallSketch';
import { PixelHitTester } from '../../modules/selection/PixelHitTester';
import { getConstraintStrategy } from '../../../../sketchObjects/constraints/constraintRegistry';
import { wallEdgeGeometry } from '../../../../sketchObjects/constraints/WallConstraint';
import { resolveFreePlacementAt, type FreeAlignMode } from '../../../../sketchObjects/constraints/FreeConstraint';
import type { AlignGuide } from '../../../../sketchObjects/constraints/alignSnap';
import { captureSketchTransformValues, createSketchTransformSnapshot, sketchTransformChanged, type SketchTransformValues } from '../../../../sketchObjects/constraints/placementCapture';
import type { PlacementValues } from '../../../../sketchObjects/constraints/types';
import type { RegisteredSketchObjectType } from '../../../../sketchObjects/registry';
import type { GizmoAxis, GizmoFrame, GizmoSpace, TransformGizmoBinding } from './TransformGizmoBinding';

/** Width/depth floor, matching the properties' validation min. */
const MIN_SIZE_M = 0.01;

/** Handle sets by constraint. Stable references — the gizmo reference-compares. */
const FREE_HANDLES: ReadonlyArray<string> = ['translation.x', 'translation.y', 'translation.free', 'rotation.z', 'rotation.view', 'scale.x', 'scale.y', 'scale.xy'];
/** Wall-attached objects: orientation comes from the wall (no rotation);
 *  arrows slide along the wall / adjust the inset (the free sphere's delta
 *  projects onto the same two wall axes). */
const WALL_HANDLES: ReadonlyArray<string> = ['translation.x', 'translation.y', 'translation.free', 'scale.x', 'scale.y', 'scale.xy'];
/** Dimensions: their size is what they measure, changed by dragging an endpoint (no scale handles). */
const DIMENSION_HANDLES: ReadonlyArray<string> = ['translation.x', 'translation.y', 'translation.free', 'rotation.z', 'rotation.view'];

export interface SketchObjectGizmoBindingDeps {
  /** Render the peer smart-guide lines of a snapped translation (the tool's
   *  existing guide overlay); called with [] to hide. */
  onGuides?: (guides: AlignGuide[], z: number) => void;
}

interface DragBaseline {
  objectId: string;
  type: RegisteredSketchObjectType;
  handleName: string;
  before: SketchTransformValues;
  /** Frame center / axes / floor at grab time (stable through the drag). */
  center: { x: number; y: number };
  dir: { x: number; y: number };
  norm: { x: number; y: number };
  floorZ: number;
  z: number;
  wallSketch: PointoramaWallSketch | null;
  /** Wall edge's usable length for offset clamping (WALL objects only). */
  edgeLength: number | null;
}

export class SketchObjectGizmoBinding implements TransformGizmoBinding {
  private object: PointoramaSketchObject | null = null;
  private baseline: DragBaseline | null = null;
  private hitTester: PixelHitTester;
  /** The gizmo drag's own wall-alignment stickiness — independent from the
   *  placement ghost's (see FreeConstraint's hoverSticky). */
  private sticky: { edgeId: string | null } = { edgeId: null };

  constructor(
    private readonly viewer: Viewer,
    private readonly deps: SketchObjectGizmoBindingDeps = {},
  ) {
    this.hitTester = new PixelHitTester(viewer);
  }

  /** Rebind to a (possibly different) object; null clears. Safe per frame. */
  setObject(object: PointoramaSketchObject | null): void {
    this.object = object;
  }

  getObject(): PointoramaSketchObject | null {
    return this.object;
  }

  // ─── TransformGizmoBinding ──────────────────────────────────────

  getFrame(): GizmoFrame | null {
    const frame = this.object?.resolveFrame();
    if (!this.object || !frame) return null;
    return {
      position: new THREE.Vector3(frame.center.x, frame.center.y, frame.z),
      quaternion: new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), Math.atan2(frame.dir.y, frame.dir.x)),
    };
  }

  getSpace(): GizmoSpace {
    // Object-aligned always: scale X/Y must run along the object's own
    // width/depth axes, in 2D and 3D alike (the object plane lies in the
    // top-down view plane, so the handles stay face-on).
    return 'object';
  }

  getEnabledHandles(): ReadonlyArray<string> | null {
    const type = this.object?.getType();
    if (!type) return FREE_HANDLES;
    if (type.dimension) return DIMENSION_HANDLES;
    return type.constraint.kind === 'WALL' ? WALL_HANDLES : FREE_HANDLES;
  }

  beginDrag(handleName: string): void {
    const object = this.object;
    const type = object?.getType();
    const frame = object?.resolveFrame();
    if (!object || !type || !frame) return;

    const before = captureSketchTransformValues(object.identifier, type);
    const wallSketch = this.viewer.scene.wallSketches.find((s) => s.groupIdentifier === object.groupIdentifier) ?? null;
    const edgeLength = before.wall && wallSketch ? (wallEdgeGeometry(wallSketch, before.wall.edgeId)?.len ?? null) : null;
    this.sticky.edgeId = null;
    this.baseline = {
      objectId: object.identifier,
      type,
      handleName,
      before,
      center: { x: frame.center.x, y: frame.center.y },
      dir: { x: frame.dir.x, y: frame.dir.y },
      norm: { x: frame.norm.x, y: frame.norm.y },
      floorZ: before.anchor?.[0]?.z ?? wallSketch?.getFloorHeight() ?? 0,
      z: frame.z,
      wallSketch,
      edgeLength,
    };
  }

  applyTranslation(worldDelta: THREE.Vector3, override: boolean): void {
    const baseline = this.baseline;
    if (!baseline) return;
    const { before } = baseline;

    if (before.wall) {
      // Slide along the wall (offset) / push off it (inset). The frame's
      // dir runs along the wall, norm away from it. The clamps are the
      // wall attachment's structural bounds — not overridable.
      const along = worldDelta.x * baseline.dir.x + worldDelta.y * baseline.dir.y;
      const away = worldDelta.x * baseline.norm.x + worldDelta.y * baseline.norm.y;
      const maxOffset = baseline.edgeLength !== null ? Math.max(0, baseline.edgeLength - before.width) : Number.POSITIVE_INFINITY;
      const wall = {
        ...before.wall,
        offset: THREE.MathUtils.clamp(before.wall.offset + along, 0, maxOffset),
        inset: Math.max(0, before.wall.inset + away),
      };
      this.writePlacement({ anchor: before.anchor, rotation: before.rotation, wall });
      return;
    }

    // FREE: run the candidate center through the same collision/assist
    // resolution the body drag uses — wall flush + corner clamp + peer
    // smart guides ('full'); Shift = raw ('none'). Dimensions overlap walls
    // by design: they always move raw.
    const candidate = { x: baseline.center.x + worldDelta.x, y: baseline.center.y + worldDelta.y };
    const placement = this.resolveFree(candidate, before.rotation, before.width, before.depth, override || baseline.type.dimension ? 'none' : 'full');
    if (!placement) return;
    this.writePlacement(placement);
    this.deps.onGuides?.(placement.guides ?? [], baseline.z);
  }

  applyRotation(worldAxis: THREE.Vector3, angle: number, override: boolean): void {
    const baseline = this.baseline;
    if (!baseline || !baseline.before.anchor) return;
    // Planar objects rotate about ±Z only (rotation.z / rotation.view both
    // resolve to it); anything else would tip the object out of the floor.
    if (Math.abs(worldAxis.z) < 0.5) return;
    const delta = angle * Math.sign(worldAxis.z);

    // Re-resolve at the SAME center with the new rotation — near a wall the
    // pose quarter-turn-aligns and stays flush (exactly what R does while
    // placing); off-wall it's a plain rotation. No peer snap ('wall' mode) —
    // that would translate the object as a side effect of rotating.
    const { before } = baseline;
    const placement = this.resolveFree(baseline.center, before.rotation + delta, before.width, before.depth, override || baseline.type.dimension ? 'none' : 'wall');
    if (!placement) return;
    this.writePlacement(placement);
  }

  applyScale(axes: ReadonlyArray<GizmoAxis>, factor: number, override: boolean): void {
    const baseline = this.baseline;
    if (!baseline) return;
    const { before, objectId } = baseline;

    const newWidth = axes.includes('x') ? Math.max(MIN_SIZE_M, before.width * factor) : before.width;
    const newDepth = axes.includes('y') ? Math.max(MIN_SIZE_M, before.depth * factor) : before.depth;

    if (before.wall) {
      // Wall-attached: width/depth are pure size parameters; keep the run
      // centered while it grows/shrinks along the wall.
      this.writeSize(getSketchObjectWidthProperty, newWidth);
      this.writeSize(getSketchObjectDepthProperty, newDepth);
      const maxOffset = baseline.edgeLength !== null ? Math.max(0, baseline.edgeLength - newWidth) : Number.POSITIVE_INFINITY;
      try {
        getSketchObjectWallOffsetProperty(objectId).value = THREE.MathUtils.clamp(before.wall.offset + (before.width - newWidth) / 2, 0, maxOffset);
      } catch {
        /* property service not ready */
      }
      return;
    }

    // FREE: re-resolve the pose at the grown size — near a wall the object
    // stays flush instead of growing INTO it ('wall' mode; Shift = raw).
    // The placement rebuild also stretches LINE anchors symmetrically
    // (their width IS the segment); the size properties track alongside.
    const placement = this.resolveFree(baseline.center, before.rotation, newWidth, newDepth, override ? 'none' : 'wall');
    if (!placement) return;
    this.writePlacement(placement);
    this.writeSize(getSketchObjectWidthProperty, newWidth);
    this.writeSize(getSketchObjectDepthProperty, newDepth);
  }

  commitDrag(): Snapshot | null {
    const baseline = this.baseline;
    this.baseline = null;
    this.sticky.edgeId = null;
    this.deps.onGuides?.([], 0);
    if (!baseline) return null;
    const after = captureSketchTransformValues(baseline.objectId, baseline.type);
    if (!sketchTransformChanged(baseline.before, after)) return null;
    // Translations can relocate the object into another room — re-file its
    // folder (handled in usePotreeEvents). Rotation/scale keep the center.
    if (baseline.handleName.startsWith('translation.')) {
      this.viewer.scene.dispatchEvent({ type: 'sketch_object_moved', value: { identifier: baseline.objectId } });
    }
    return createSketchTransformSnapshot(baseline.objectId, baseline.type.constraint.kind, baseline.before, after);
  }

  cancelDrag(): void {
    this.baseline = null;
    this.sticky.edgeId = null;
    this.deps.onGuides?.([], 0);
  }

  // ─── Internals ──────────────────────────────────────────────────

  /** The shared FREE placement resolution at a candidate pose. */
  private resolveFree(point: { x: number; y: number }, rotation: number, width: number, depth: number, mode: FreeAlignMode): ReturnType<typeof resolveFreePlacementAt> {
    const baseline = this.baseline;
    if (!baseline) return null;
    return resolveFreePlacementAt(this.viewer, this.hitTester, baseline.wallSketch, baseline.floorZ, baseline.objectId, baseline.type, point, rotation, width, depth, this.sticky, mode);
  }

  private writePlacement(values: PlacementValues): void {
    const baseline = this.baseline;
    if (!baseline) return;
    try {
      getConstraintStrategy(baseline.type.constraint.kind).writePlacement(baseline.objectId, values);
    } catch {
      /* property service not ready */
    }
  }

  private writeSize(getter: (id: string) => { value: number | undefined }, value: number): void {
    const baseline = this.baseline;
    if (!baseline) return;
    try {
      getter(baseline.objectId).value = value;
    } catch {
      /* property service not ready */
    }
  }
}
